import confirmationPopupGraphic from '../../assets/icons/confirmation-popup-graphic.svg';
import notificationPopupErrorGraphic from '../../assets/icons/notification-popup-error-graphic.svg';
import tickIcon from '../../assets/icons/tick-icon.svg';
import cancelIcon from '../../assets/icons/cancel-icon.svg';
import newGameAddIcon from '../../assets/icons/new-game-add-icon.svg';
import closeIcon from '../../assets/icons/close-icon.svg'
import deleteIcon from '../../assets/icons/drafts-delete-icon.svg';
import './index.scss';
import React, { ReactElement, ReactNode, RefObject, useEffect, useState, useRef, MutableRefObject } from 'react';
import { AnimatePresence } from 'framer-motion';
import { IAttributesPopupProps, IAttributesUploadPopupProps, IConfirmationPopupProps, IConfirmationPopupWithCommentsProps, IElementPopupProps, INewGamePopupProps, INotificationPopupProps } from '../../interfaces/popup';
import { useAppDispatcher, useAppSelector } from '../../hooks/redux';
import { IGameElementInfo, IGameInfo, IGameDraftCreateResponse, IGameCheckerApprovalResponse, IGameCheckerRejectionResponse, IGameMainFields, GameOperation } from '../../interfaces/game-info';
import { Accordion, ElementsSubAccordion, SubAccordion } from '../Accordion';
import { ICoreElementProperties, IElementPeriodInfo, IElementsInfo, IndexedElementsInfo } from '../../interfaces/elements';
import { IAttributesInfo, IndexedAttributesInfo } from '../../interfaces/attributes';
import { NoDataAvailable } from '../NoDataAvailable';
import { Upload, UploadFileInfo, UploadFileStatus, UploadOnAddEvent, UploadOnStatusChangeEvent, UploadResponse } from "@progress/kendo-react-upload";
import { checkAPIResponse, formatURL, generateRandomInt32Number } from '../../utils/utils';
import { config } from '../../utils/config';
import { createAttribute, deleteAttribute, getAttributesList } from '../../api/attributes';
import { IAPIResponse, IAttributeCreateResponse, IFileUploadResponse } from '../../interfaces/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons';
import { addAttributesList } from '../../slices/properties';
import { DropDownList, DropDownListChangeEvent, DropDownListProps, ListItemProps, MultiSelectProps } from '@progress/kendo-react-dropdowns';
import { createDraft, updateDraft } from '../../api/draft';
import { IndexedElementPropertiesInfo, IndexedKindInfo, IndexedPeriodInfo, IndexedScopeInfo, IUserInfoState } from '../../interfaces/slices';
import { approveGame, rejectGame } from '../../api/makerChecker';
import { Tooltip } from "@progress/kendo-react-tooltip";
import { IndexedTeamsInfo, ITeamsInfo } from '../../interfaces/teams';
import { TextArea, TextAreaChangeEvent } from "@progress/kendo-react-inputs";
import { useForm } from "react-hook-form";
import { InputStringController, InputNumberController, DropdownController, MultiselectController } from '../Form/Controllers';
import { log } from '../../utils/logger';
import { useRefreshEventSubscriber } from '../../hooks/rxjs';
import { AnimatedPopup } from '../Animated';

export const ConfirmationPopup = (props: IConfirmationPopupProps) => {
    return <div className="confirmation-popup-container">
        <AnimatedPopup className="confirmation-popup-wrapper">
            <div className="confirmation-popup-graphic-container">
                <img src={confirmationPopupGraphic} alt='' />
            </div>
            <div className="confirmation-popup-title-container">
                <span>{props.title}</span>
            </div>
            <div className="confirmation-popup-message-container">
                <span>{props.message}</span>
            </div>
            <div className="confirmation-popup-buttons-container">
                <div className='confirmation-popup-cancel-button-container'>
                    <span onClick={() => props.onCancellation()}>Cancel</span>
                </div>
                <div className='confirmation-popup-confirm-button-container'>
                    <span onClick={() => props.onConfirmation()}>Confirm</span>
                </div>
            </div>
        </AnimatedPopup>
    </div>
}

export const NotificationPopup = (props: INotificationPopupProps) => {
    return <div className="notification-popup-container">
        <AnimatedPopup className="notification-popup-wrapper">
            <div className="notification-popup-graphic-container">
                <img src={notificationPopupErrorGraphic} alt='' />
            </div>
            <div className="notification-popup-message-container">
                <span>{props.message}</span>
            </div>
            <div className="notification-popup-buttons-container">
                <div className='notification-popup-close-button-container'>
                    <span onClick={() => props.closePopup()}>Close</span>
                </div>
            </div>
        </AnimatedPopup>
    </div>
}

export const ConfirmationPopupWithReason = (props: IConfirmationPopupWithCommentsProps) => {
    const [reason, setReason] = useState<string>('');

    const onConfirmation = (): void => {
        if (props.isModifyReasonRequired === true && reason === "") {
            return;
        }
        else {
            props.onConfirmation(reason);
        }
    }

    return <div className="confirmation-popup-container">
        <AnimatedPopup className="confirmation-popup-wrapper">
            <div className="confirmation-popup-graphic-container">
                <img src={confirmationPopupGraphic} alt='' />
            </div>
            <div className="confirmation-popup-title-container">
                <span>{props.title}</span>
            </div>
            <div className="confirmation-popup-message-container">
                <span>{props.message}</span>
            </div>
            {props.isModifyReasonRequired === true &&
                <div className="confirmation-popup-reason-container">
                    <TextArea
                        required={true}
                        // valid={valid}
                        // type={type}
                        // id={id}
                        // autoSize={true}
                        value={reason}
                        disabled={false}
                        maxLength={255}
                        rows={4}
                        placeholder={'Enter a reason'}
                        className={`confirmation-popup-reason-textarea`}
                        onChange={(event: TextAreaChangeEvent) => setReason(event.value)}
                    />
                </div>
            }
            <div className="confirmation-popup-buttons-container">
                <div className='confirmation-popup-cancel-button-container'>
                    <span onClick={() => props.onCancellation()}>Cancel</span>
                </div>
                <div className='confirmation-popup-confirm-button-container'>
                    <span onClick={onConfirmation}>Confirm</span>
                </div>
            </div>
        </AnimatedPopup>
    </div>
}

export const NewGamePopup = (props: INewGamePopupProps) => {
    const { control, handleSubmit, formState: { errors, isValid }, reset, clearErrors, setValue, unregister, resetField, getValues, trigger } = useForm();

    const userInfo: IUserInfoState = useAppSelector((state) => state.userInfo);
    const teamsInfo: IndexedTeamsInfo = useAppSelector((state) => state.gameProperties.teams);
    const elementsInfo: IndexedElementsInfo = useAppSelector((state) => state.gameProperties.elements);
    const mainGameValidations: Array<IGameMainFields> = useAppSelector((state) => state.gameProperties.mainGameFieldValidations);
    const elementPropertiesValidations: IndexedElementPropertiesInfo = useAppSelector((state) => state.gameProperties.elementPropertiesValidations);

    const [confirmationPopupVisibility, setConfirmationPopupVisibility] = useState<{ visibility: boolean, title: string, message: string, isReasonRequired: boolean, onConfirmation: Function, onCancellation: Function }>({ visibility: false, title: '', message: '', isReasonRequired: false, onConfirmation: () => { }, onCancellation: () => { } });
    const [errorPopupVisibility, setErrorPopupVisibility] = useState<{ visibility: boolean, message: string }>({ visibility: false, message: '' });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [elementPopupDetails, setElementPopupDetails] = useState<{ visibility: boolean, gameInfo: IGameElementInfo | null, operation: 'view' | 'create' | 'update' }>({ visibility: false, gameInfo: null, operation: 'create' })

    const [elementToIdMap, setElementToIdMap] = useState<{ [key: string]: Array<string> }>({});
    const [elementIdToDetailsMap, setElementIdToDetailsMap] = useState<{ [key: string]: IGameElementInfo }>({});

    /**
     * Only applicable for create, update and delete operations, not applicable for approval/rejection reason
     * @returns 
     */
    const isReasonRequired = (): boolean => {
        if (props.updationGameInfo == null) {
            return false;
        }
        else {
            const currentOperation: GameOperation = props.updationGameInfo.operation;
            if (currentOperation.toLowerCase() === 'create') {
                return true;
            }
            else if (currentOperation.toLowerCase() === 'update') {
                return true;
            }
            else if (currentOperation.toLowerCase() === 'delete') {
                return true;
            }
            else {
                log.error(`Unknown operation mode in data (${currentOperation})`)
                return true;
            }
        }
    }

    const getOperation = (): GameOperation => {
        if (props.isApprovalMode === true && props.updationGameInfo != null) {
            return props.updationGameInfo.operation;
        }
        else if (props.updationGameInfo == null) {
            return 'create';
        }
        else {
            const currentOperation: GameOperation = props.updationGameInfo.operation;
            if (currentOperation.toLowerCase() === 'create') {
                return 'update';
            }
            else if (currentOperation.toLowerCase() === 'update') {
                return 'update';
            }
            else if (currentOperation.toLowerCase() === 'delete') {
                return 'delete';
            }
            else {
                log.error(`Unknown operation mode in data (${currentOperation})`)
                return 'update';
            }
        }
    }

    const isPropertyDisabled = (): boolean => {
        let isDisabled: boolean = false;

        if (props.isApprovalMode === true && props.updationGameInfo != null) {
            isDisabled = true;
        }
        else if (props.updationGameInfo == null) {
            isDisabled = false;
        }
        else {
            const currentOperation: GameOperation = props.updationGameInfo.operation;
            if (currentOperation.toLowerCase() === 'create') {
                isDisabled = false;
            }
            else if (currentOperation.toLowerCase() === 'update') {
                isDisabled = false;
            }
            else if (currentOperation.toLowerCase() === 'delete') {
                isDisabled = true;
            }
            else {
                log.error(`Unknown operation mode in data (${currentOperation})`)
                isDisabled = false;
            }
        }

        return isLoading === true || isDisabled === true;
    }

    const areElementsEditable = (): boolean => {
        if (props.isApprovalMode === true && props.updationGameInfo != null) {
            return false;
        }
        else if (props.updationGameInfo == null) {
            return true;
        }
        else {
            const currentOperation: GameOperation = props.updationGameInfo.operation;
            if (currentOperation.toLowerCase() === 'create') {
                return true;
            }
            else if (currentOperation.toLowerCase() === 'update') {
                return true;
            }
            else if (currentOperation.toLowerCase() === 'delete') {
                return false;
            }
            else {
                log.error(`Unknown operation mode in data (${currentOperation})`)
                return true;
            }
        }
    }

    const onInvalidForm = (errors: any): void => {
        if (errors != null && Object.entries(errors).length > 0) {
            trigger();
        }
    }

    const onVerifyGameDetails = (data: any): void => {
        const foundStatus = Object.entries(elementToIdMap).find((item: [string, Array<string>]) => {
            const minValue = elementPropertiesValidations[item[0]].min;

            if (item[1].length < minValue) {
                setErrorPopupVisibility({ message: ` ${elementsInfo[item[0]].name} must have a minimum of 1 element created`, visibility: true });
                setIsLoading(false);
                return true;
            }
            else {
                return false;
            }
        })

        if (foundStatus != null) {
            return;
        }

        setConfirmationPopupVisibility({ visibility: true, title: 'Save to Drafts', message: 'Confirm to save', isReasonRequired: isReasonRequired(), onConfirmation: saveAsDraft, onCancellation: resetConfirmationPopup });
    }

    const onElementModified = (elementProperties: Array<IGameElementInfo>): void => {
        hideNewElementPanel();

        const existingElementToIdMap: { [key: string]: Array<string> } = { ...elementToIdMap };
        const existingElementIdToDetailsMap: { [key: string]: IGameElementInfo } = { ...elementIdToDetailsMap };

        elementProperties.forEach((elemProps: IGameElementInfo, index: number) => {
            const existingMapArray = existingElementToIdMap[elemProps.type];
            if (existingMapArray.includes(elemProps.id) === false) {
                existingMapArray.push(elemProps.id);
            }

            existingElementToIdMap[elemProps.type] = existingMapArray;
            existingElementIdToDetailsMap[elemProps.id] = elemProps;
        });

        setElementToIdMap(existingElementToIdMap);
        setElementIdToDetailsMap(existingElementIdToDetailsMap);
    }

    const onDeleteElement = (id: string, elementType: string): void => {
        const existingElementToIdMap: { [key: string]: Array<string> } = { ...elementToIdMap };
        const existingElementIdToDetailsMap: { [key: string]: IGameElementInfo } = { ...elementIdToDetailsMap };

        let existingMapArray = existingElementToIdMap[elementType];

        existingMapArray = existingMapArray.filter(item => item !== id)

        delete existingElementIdToDetailsMap[id];

        existingElementToIdMap[elementType] = [];

        Object.values(existingElementIdToDetailsMap).forEach((item: IGameElementInfo) => {
            if (item.type === elementType) {
                existingElementToIdMap[elementType].push(item.id);
            }
        });

        setElementToIdMap(existingElementToIdMap);
        setElementIdToDetailsMap(existingElementIdToDetailsMap);
    }

    const onEditElement = (id: string, elementType: string): void => {
        if (elementIdToDetailsMap[id] != null) {
            showNewElementPanel(true, elementIdToDetailsMap[id]);
        }
    }

    const onViewElement = (id: string, elementType: string): void => {
        if (elementIdToDetailsMap[id] != null) {
            setElementPopupDetails({ visibility: true, gameInfo: elementIdToDetailsMap[id], operation: 'view' });
        }
    }

    const hideNewElementPanel = (): void => {
        setElementPopupDetails({ visibility: false, gameInfo: null, operation: 'create' });
    }

    const showNewElementPanel = (isUpdatingElement: boolean, updateInfo: IGameElementInfo | null): void => {
        setElementPopupDetails({ visibility: true, gameInfo: updateInfo, operation: isUpdatingElement === true ? 'update' : 'create' });
    }

    const resetConfirmationPopup = (): void => {
        setConfirmationPopupVisibility({ visibility: false, title: '', message: '', isReasonRequired: false, onConfirmation: () => { }, onCancellation: () => { } })
    }

    const saveAsDraft = async (reason: string = ''): Promise<void> => {
        setIsLoading(true);
        resetConfirmationPopup();

        let gameElements: Array<IGameElementInfo> = [];

        Object.values(elementIdToDetailsMap).forEach((item: IGameElementInfo) => {
            gameElements.push(item);
        })

        if (isReasonRequired() && (reason == null || reason === "")) {
            setErrorPopupVisibility({ message: 'Please enter a reason before saving', visibility: true });
            setIsLoading(false);
            return;
        }

        const formValues: any = getValues();
        let elementProperties: any = {};

        Object.entries(formValues).forEach((item: [string, any], index: number) => {
            const fieldProperties: IGameMainFields | undefined = mainGameValidations.find((fieldItem: IGameMainFields) => fieldItem.name === item[0].toString());

            if (fieldProperties != null) {
                if (fieldProperties.properties.type === 'dropdown-multiple') {
                    elementProperties[item[0]] = [];

                    const selectedEvents: Array<ITeamsInfo> | null = item[1] as Array<ITeamsInfo> | null;

                    if (selectedEvents != null && selectedEvents.length > 0) {
                        selectedEvents.forEach((info: ITeamsInfo, index: number) => {
                            elementProperties[item[0]].push(info.id);
                        })
                    }
                }
                else if (fieldProperties.properties.type === 'dropdown') {
                    elementProperties[item[0]] = item[1]["id"];
                }
                else if (fieldProperties.properties.type === 'number') {
                    elementProperties[item[0]] = Number(item[1]);
                }
                else {
                    elementProperties[item[0]] = item[1];
                }
            }

        });

        let gameInfo: IGameInfo = {
            gameName: elementProperties['gameName'],
            description: elementProperties['description'],
            operation: getOperation(),
            elements: gameElements,
            teamId: elementProperties["teams"],
            acceptRejectReason: '',
            modifyReason: isReasonRequired() === true ? reason : '',
            sendForApprovalReason: '',
        }

        if (isReasonRequired() === true && props.updationGameInfo != null) {
            gameInfo.version = props.updationGameInfo.version;
        }

        let apiResponse: IAPIResponse<IGameDraftCreateResponse>;

        if (isReasonRequired() === true) {
            apiResponse = await updateDraft(userInfo.lanid, gameInfo);
        }
        else {
            apiResponse = await createDraft(userInfo.lanid, gameInfo);
        }

        const formattedResponse = checkAPIResponse<IGameDraftCreateResponse>(apiResponse);

        if (formattedResponse.status === true) {
            setIsLoading(false);
            closePopup();
        }
        else {
            setErrorPopupVisibility({ message: `An error occurred while trying to ${getOperation().toLowerCase()} the game (${formattedResponse.message})`, visibility: true });
            setIsLoading(false);
        }
    }

    const closePopup = (): void => {
        props.closePopup();
    }

    const getPropertyDisabledState = (propertyName: string): boolean => {
        switch (propertyName) {
            case 'gameName':
                return isPropertyDisabled() === true || (props.updationGameInfo != null && (props.updationGameInfo.operation.toLowerCase() === 'create' || props.updationGameInfo.operation.toLowerCase() === 'update' || props.updationGameInfo.operation.toLowerCase() === 'delete'));
            case 'description':
            case 'teams':
                return isPropertyDisabled() === true;
            default:
                return false;
        }
    }

    const getDefaultValueForProperty = (propertyName: string, defaultValue: string) => {
        if (isReasonRequired() === true && props.updationGameInfo != null) {
            const elementInfo: IGameInfo = props.updationGameInfo;

            switch (propertyName) {
                case 'teams':
                    let teamInfo: Array<ITeamsInfo> = [];

                    if (elementInfo.teamId != null && elementInfo.teamId.length > 0) {
                        const elemInfo: Array<string> = elementInfo.teamId;

                        Object.values(teamsInfo).forEach((info: ITeamsInfo) => {
                            if (elemInfo.includes(info.id) === true) {
                                teamInfo.push(info);
                            }
                        });
                    }

                    if (teamInfo.length > 0) {
                        return teamInfo;
                    }
                    else {
                        return null;
                    }
                default:
                    // @ts-ignore
                    return props.updationGameInfo[propertyName];
            }

        }
        else {
            switch (propertyName) {
                case 'teams':
                    return teamsInfo[defaultValue];
                default:
                    return defaultValue;
            }
        }
    }

    const getMultiSelectProps = (elementPropertyName: string): MultiSelectProps => {
        if (elementPropertyName === 'teams') {
            return {
                data: Object.values(teamsInfo),
                textField: "name",
                dataItemKey: "id",
            }
        }
        else {
            return {};
        }
    }

    const checkMultiSelectValue = (propertyName: string, value: any, depends: string | null): undefined | any[] => {
        if (value == null || typeof (value) === 'object') {
            return value;
        }
        else if (typeof (value) === 'string') {
            if (value === '') {
                return undefined;
            }
            else {
                return undefined;
            }
        }
    }

    const doesPropertyHaveErrors = (propertyName: string): boolean => {
        if (errors != null && errors[propertyName] != null) {
            return true;
        }
        else {
            return false;
        }
    }

    const generateErrorText = (property: IGameMainFields): string => {
        if (errors != null && errors[property.name] != null && errors[property.name].type != null) {
            switch (errors[property.name].type) {
                case 'required':
                    return `${property.title} is required`;
                case 'maxLength':
                    return `${property.title} should contain at most ${property.properties.max} characters`;
                case 'minLength':
                    return `${property.title} should contain at least ${property.properties.min} characters`;
                case 'max':
                    return `${property.title} has to have a maximum value of ${property.properties.max}`;
                case 'min':
                    return `${property.title} has to have a minimum value of ${property.properties.min}`;
                case 'pattern':
                case 'validate':
                    return `Special characters are not allowed`;
                default:
                    return ``;
            }
        }
        else {
            return ``;
        }
    }

    const onApprove = async (reason: string): Promise<void> => {
        if (props.updationGameInfo != null) {
            setIsLoading(true);

            const apiResponse: IAPIResponse<IGameCheckerApprovalResponse> = await approveGame(userInfo.lanid, props.updationGameInfo.gameName, reason);
            const formattedResponse = checkAPIResponse<IGameCheckerRejectionResponse>(apiResponse);

            if (formattedResponse.status === true) {
                resetConfirmationPopup();
                closePopup();
            }
            else {
                setErrorPopupVisibility({ message: `An error occurred while trying to approve the game (${formattedResponse.message})`, visibility: true });
                resetConfirmationPopup();
            }

            setIsLoading(false);
        }
    }

    const onReject = async (reason: string): Promise<void> => {
        if (props.updationGameInfo != null) {
            setIsLoading(true);

            const apiResponse: IAPIResponse<IGameCheckerRejectionResponse> = await rejectGame(userInfo.lanid, props.updationGameInfo.gameName, reason);
            const formattedResponse = checkAPIResponse<IGameCheckerRejectionResponse>(apiResponse);

            if (formattedResponse.status === true) {
                resetConfirmationPopup();
                closePopup();
            }
            else {
                setErrorPopupVisibility({ message: `An error occurred while trying to reject the game (${formattedResponse.message})`, visibility: true });
                resetConfirmationPopup();
            }

            setIsLoading(false);
        }
    }

    useEffect(() => {
        let elementMap: { [key: string]: Array<string> } = {};
        let elementDetailsMap: { [key: string]: IGameElementInfo } = {}

        Object.keys(elementPropertiesValidations).forEach((elementId: string, index: number) => {
            elementMap[elementId] = [];
        });

        if (props.updationGameInfo != null) {
            props.updationGameInfo.elements.forEach((elementInfo: IGameElementInfo, index: number) => {
                if (elementMap[elementInfo.type] != null) {
                    elementMap[elementInfo.type].push(elementInfo.id);
                }
                else {
                    elementMap[elementInfo.type] = [];
                    elementMap[elementInfo.type].push(elementInfo.id);
                }

                elementDetailsMap[elementInfo.id] = elementInfo;
            });
        }

        setElementToIdMap(elementMap);
        setElementIdToDetailsMap(elementDetailsMap);
    }, [])

    return <div className='new-game-popup-container'>
        <AnimatedPopup className='new-game-popup-wrapper'>
            <div className='row-1 extended'>
                {props.isApprovalMode === false &&
                    <div className={`new-element-container ${isPropertyDisabled() === true ? `disabled` : ``}`} onClick={() => showNewElementPanel(false, null)}>
                        <img src={newGameAddIcon} alt='' />
                        <span>New Element</span>
                    </div>
                }
            </div>
            <div className='row-2 extended'>
                <div className='form-container'>
                    {mainGameValidations.map((item: IGameMainFields) => (
                        <div className='form-wrapper' key={item.name}>
                            <div className='form-row'>
                                <div className={`form-row-title ${doesPropertyHaveErrors(item.name) === true ? `error` : ``}`}>
                                    <span>{item.title}</span>
                                    {/* <Tooltip anchorElement="target" className='tooltip-custom'>
                                        <FontAwesomeIcon icon={faCircleInfo} color={'#9F9F9F'} title={''} />
                                    </Tooltip> */}
                                </div>
                                <div className={`form-row-text-value ${item.properties.type === 'dropdown' || item.properties.type === 'dropdown-multiple' ? `with-dropdown` : ``} ${doesPropertyHaveErrors(item.name) === true ? `error` : ``}`}>
                                    <>
                                        {item.properties.type === 'string' &&
                                            <InputStringController
                                                control={control}
                                                item={item}
                                                getDefaultValue={getDefaultValueForProperty}
                                                disabled={getPropertyDisabledState(item.name)}
                                                uniquenessValidationFunction={null}
                                            />
                                        }
                                        {item.properties.type === 'dropdown-multiple' &&
                                            <MultiselectController
                                                control={control}
                                                item={item}
                                                getDefaultValue={getDefaultValueForProperty}
                                                disabled={getPropertyDisabledState(item.name)}
                                                checkMultiSelectValue={checkMultiSelectValue}
                                                getMultiSelectProps={getMultiSelectProps}
                                                className={`form-row-multiselect-component`}
                                                popupSettings={{ className: 'form-row-dropdown-popup', popupClass: 'form-row-dropdown-popup-element' }}
                                            />
                                        }
                                    </>
                                </div>
                            </div>
                            {doesPropertyHaveErrors(item.name) &&
                                <div className={`form-row-error`}>
                                    <span>{generateErrorText(item)}</span>
                                </div>
                            }
                        </div>
                    ))}
                    <div className='form-wrapper'>
                        <div className='form-row'>
                            <div className={`form-row-title`}>
                                <span>operation</span>
                                {/* <Tooltip anchorElement="target" className='tooltip-custom'>
                                    <FontAwesomeIcon icon={faCircleInfo} color={'#9F9F9F'} title={''} />
                                </Tooltip> */}
                            </div>
                            <div className={`form-row-text-value`}>
                                <input type="text" value={getOperation().toUpperCase()} disabled={true} />
                            </div>
                        </div>
                    </div>
                    {Object.keys(elementToIdMap).length > 0 &&
                        <>
                            {Object.keys(elementToIdMap).map((item: string, index: number) => (
                                <div className='form-wrapper' key={item}>
                                    <div className='form-row with-accordion'>
                                        <Accordion
                                            key={item}
                                            icon=''
                                            title={elementsInfo[item].name}
                                            count={`${elementToIdMap[item].length}/${elementPropertiesValidations[item].max}`}
                                            content={
                                                <>
                                                    {elementToIdMap[item] != null && elementToIdMap[item].length > 0 &&
                                                        <>
                                                            {elementToIdMap[item].map((elementId: string, index: number) => (
                                                                <>
                                                                    {elementIdToDetailsMap[elementId] != null &&
                                                                        <>
                                                                            {areElementsEditable() === true ?
                                                                                <ElementsSubAccordion
                                                                                    key={elementId}
                                                                                    title={elementIdToDetailsMap[elementId].name}
                                                                                    icon={null}
                                                                                    content={''}
                                                                                    onDeleteButtonClick={() => onDeleteElement(elementId, elementIdToDetailsMap[elementId].type)}
                                                                                    onEditButtonClick={() => onEditElement(elementId, elementIdToDetailsMap[elementId].type)}
                                                                                    isDisabled={isPropertyDisabled() === true}
                                                                                />
                                                                                :
                                                                                <SubAccordion
                                                                                    key={elementId}
                                                                                    title={elementIdToDetailsMap[elementId].name}
                                                                                    icon={null}
                                                                                    content={''}
                                                                                    onViewButtonClick={() => onViewElement(elementId, elementIdToDetailsMap[elementId].type)}
                                                                                />
                                                                            }
                                                                        </>

                                                                    }
                                                                </>
                                                            ))}
                                                        </>
                                                    }
                                                </>
                                            }
                                        />
                                    </div>
                                </div>
                            ))}
                        </>
                    }
                </div>
                {isLoading === true ?
                    <div className='confirmation-container'>
                        <div className='loading-container' title='Saving as draft'>
                            <FontAwesomeIcon icon={faSpinnerThird} size="3x" color='#9D7EB4' spin />
                        </div>
                    </div>
                    :
                    <>
                        {props.isApprovalMode === true ?
                            <>
                                {userInfo.roles.checker === true ?
                                    <div className='checker-confirmation-container'>
                                        <div className='button accept' onClick={() => setConfirmationPopupVisibility({ visibility: true, title: 'Approve Game?', message: 'Are you sure you want to approve this game?', isReasonRequired: true, onConfirmation: onApprove, onCancellation: resetConfirmationPopup })} >
                                            <span>Approve</span>
                                        </div>
                                        <div className='button reject' onClick={() => setConfirmationPopupVisibility({ visibility: true, title: 'Reject Game?', message: 'Are you sure you want to reject this game?', isReasonRequired: true, onConfirmation: onReject, onCancellation: resetConfirmationPopup })}  >
                                            <span>Reject</span>
                                        </div>
                                        <div className='button rounded'>
                                            <img src={cancelIcon} alt='' onClick={closePopup} />
                                        </div>
                                    </div>
                                    :
                                    <div className='confirmation-container'>
                                        <div className='button'>
                                            <img src={cancelIcon} alt='' onClick={closePopup} />
                                        </div>
                                    </div>
                                }
                            </>
                            :
                            <>
                                {props.updationGameInfo != null && props.updationGameInfo.operation.toLowerCase() === 'delete' ?
                                    <div className='confirmation-container'>
                                        <div className='button'>
                                            <img src={cancelIcon} alt='' onClick={closePopup} />
                                        </div>
                                    </div>
                                    :
                                    <div className='confirmation-container'>
                                        <div className='button'>
                                            <img src={tickIcon} alt='' onClick={handleSubmit(onVerifyGameDetails, onInvalidForm)} />
                                        </div>
                                        <div className='button'>
                                            <img src={cancelIcon} alt='' onClick={() => setConfirmationPopupVisibility({ visibility: true, title: 'Are you sure?', message: 'All unsaved data will be lost', isReasonRequired: false, onConfirmation: () => { resetConfirmationPopup(); closePopup(); }, onCancellation: resetConfirmationPopup })} />
                                        </div>
                                    </div>
                                }
                            </>

                        }
                    </>
                }
            </div>
        </AnimatedPopup>
        <AnimatePresence>
            {confirmationPopupVisibility.visibility === true &&
                <ConfirmationPopupWithReason
                    title={confirmationPopupVisibility.title}
                    message={confirmationPopupVisibility.message}
                    onConfirmation={confirmationPopupVisibility.onConfirmation}
                    onCancellation={confirmationPopupVisibility.onCancellation}
                    isModifyReasonRequired={confirmationPopupVisibility.isReasonRequired}
                />
            }
            {errorPopupVisibility.visibility === true &&
                <NotificationPopup
                    message={errorPopupVisibility.message}
                    closePopup={() => setErrorPopupVisibility({ visibility: false, message: '' })}
                />
            }
            {elementPopupDetails.visibility === true &&
                <ElementPopup
                    operation={elementPopupDetails.operation}
                    updateElementInfo={elementPopupDetails.gameInfo}
                    onCancelEvent={hideNewElementPanel}
                    onConfirmEvent={(elementProperties: IGameElementInfo) => onElementModified([elementProperties])}
                    onCloseEvent={hideNewElementPanel}
                    existingElementInfo={Object.values(elementIdToDetailsMap)}
                />
            }
        </AnimatePresence>
    </div >
}

export const AttributesPopup = (props: IAttributesPopupProps) => {
    const dispatcher = useAppDispatcher();
    const lanid: string = useAppSelector((state) => state.userInfo.lanid);
    const attributesInfo: IndexedAttributesInfo = useAppSelector((state) => state.gameProperties.attributes);
    const [uploadPopupVisibility, setUploadPopupVisibility] = useState<boolean>(false);
    const [attributesUI, setAttributesUI] = useState<ReactElement>(<></>);
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ visibility: boolean, title: string, message: string, onConfirmation: Function, onCancellation: Function }>({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } });

    const getAttributesListUI = (): void => {
        let ui: Array<ReactElement> = [];
        const items: Array<IAttributesInfo> = Object.values(attributesInfo);

        for (let i = 0; i < items.length; i += 3) {
            ui.push(
                <div className='attribute-row-container' key={`attribute-row-container-${items[i].id}`}>
                    <div className='attribute-row-wrapper'>
                        <div className='attribute-img-container'>
                            <img src={items[i].url} alt="" />
                        </div>
                        <div className='attribute-name-container'>
                            <span title={items[i].name}>{items[i].name}</span>
                        </div>
                        <div className='attribute-delete-container'>
                            <Tooltip anchorElement="target" className='tooltip-custom'>
                                <img src={deleteIcon} alt='' title='Delete' onClick={() => setDeleteConfirmation({
                                    visibility: true,
                                    title: `Confirm to delete?`,
                                    message: `Confirm to delete attribute ${items[i].name}`,
                                    onConfirmation: () => removeAttribute(items[i].id),
                                    onCancellation: () => setDeleteConfirmation({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } })
                                })} />
                            </Tooltip>
                        </div>
                    </div>
                    {items[i + 1] != null &&
                        <div className='attribute-row-wrapper'>
                            <div className='attribute-img-container'>
                                <img src={items[i + 1].url} alt="" />
                            </div>
                            <div className='attribute-name-container'>
                                <span title={items[i + 1].name}>{items[i + 1].name}</span>
                            </div>
                            <div className='attribute-delete-container'>
                                <Tooltip anchorElement="target" className='tooltip-custom'>
                                    <img src={deleteIcon} alt='' title='Delete' onClick={() => setDeleteConfirmation({
                                        visibility: true,
                                        title: `Confirm to delete?`,
                                        message: `Confirm to delete attribute ${items[i + 1].name}`,
                                        onConfirmation: () => removeAttribute(items[i + 1].id),
                                        onCancellation: () => setDeleteConfirmation({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } })
                                    })} />
                                </Tooltip>
                            </div>
                        </div>
                    }
                    {items[i + 2] != null &&
                        <div className='attribute-row-wrapper'>
                            <div className='attribute-img-container'>
                                <img src={items[i + 2].url} alt="" />
                            </div>
                            <div className='attribute-name-container'>
                                <span title={items[i + 2].name}>{items[i + 2].name}</span>
                            </div>
                            <div className='attribute-delete-container'>
                                <Tooltip anchorElement="target" className='tooltip-custom'>
                                    <img src={deleteIcon} alt='' title='Delete' onClick={() => setDeleteConfirmation({
                                        visibility: true,
                                        title: `Confirm to delete?`,
                                        message: `Confirm to delete attribute ${items[i + 2].name}`,
                                        onConfirmation: () => removeAttribute(items[i + 2].id),
                                        onCancellation: () => setDeleteConfirmation({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } })
                                    })} />
                                </Tooltip>
                            </div>
                        </div>
                    }
                </div>
            );
        }

        setAttributesUI(<div className='attributes-display-container'>{ui}</div>);
    }

    const onUpdateAttributesList = async (): Promise<void> => {
        const response: IAPIResponse<Array<IAttributesInfo>> = await getAttributesList<IAttributesInfo>(lanid);
        const formattedResponse = checkAPIResponse<Array<IAttributesInfo>>(response);

        if (formattedResponse.status === true && response.response != null && response.response.data != null) {
            dispatcher(addAttributesList(response.response.data));
        }
        else {
            if (formattedResponse.message != null) {
                log.Error(`Unable to load attributes (${formattedResponse.message})`);
            }
            else {
                log.Error(`Unable to load attributes`);
            }
        }
    }

    const removeAttribute = async (attributeId: number): Promise<void> => {
        const response: IAPIResponse<null> = await deleteAttribute(lanid, attributeId);
        const formattedResponse = checkAPIResponse<null>(response);

        setDeleteConfirmation({ visibility: false, title: '', message: '', onConfirmation: () => { }, onCancellation: () => { } });

        if (formattedResponse.status === false) {
            log.Error(`Unable to delete attribute (${formattedResponse.message})`);
        }
    }

    useEffect(() => {
        getAttributesListUI();
    }, [attributesInfo]);

    useRefreshEventSubscriber('attribute', onUpdateAttributesList);

    return <div className='attributes-popup-container'>
        <AnimatedPopup className='attributes-popup-wrapper'>
            <div className='row-1 extended'>
                <div className="close-container" onClick={() => props.closePopup()}>
                    <img src={closeIcon} alt='' />
                </div>
            </div>
            <div className='row-2 extended'>
                <div className='heading-container'>
                    <span>Attributes</span>
                </div>
                <div className='info-container'>
                    {Object.keys(attributesInfo).length > 0 ? attributesUI : <NoDataAvailable message='No attributes uploaded' />}
                </div>
                <div className='upload-button-container'>
                    <div className='upload-button-wrapper' title="Upload a new attribute" onClick={() => setUploadPopupVisibility(true)}>
                        <img src={newGameAddIcon} alt='' />
                        <span>Upload</span>
                    </div>
                </div>
            </div>
        </AnimatedPopup>
        <AnimatePresence>
            {uploadPopupVisibility === true &&
                <AttributesUploadPopup
                    onCancelEvent={() => setUploadPopupVisibility(false)}
                    onConfirmEvent={() => { onUpdateAttributesList(); setUploadPopupVisibility(false); }}
                />
            }
            {deleteConfirmation.visibility === true &&
                <ConfirmationPopupWithReason
                    isModifyReasonRequired={false}
                    title={deleteConfirmation.title}
                    message={deleteConfirmation.message}
                    onConfirmation={deleteConfirmation.onConfirmation}
                    onCancellation={deleteConfirmation.onCancellation}
                />
            }
        </AnimatePresence>
    </div>
}

export const AttributesUploadPopup = (props: IAttributesUploadPopupProps) => {
    const { control, handleSubmit, formState: { errors }, getValues, trigger } = useForm();
    const uploadRef: RefObject<Upload> = React.createRef<Upload>();
    const lanid: string = useAppSelector((state) => state.userInfo.lanid);
    const attributesValidationInfo: ICoreElementProperties = useAppSelector((state) => state.gameProperties.attributePropertiesValidations);
    const generatedId: MutableRefObject<number> = useRef<number>(0);

    const [isFileUploadError, setIsFileUploadError] = useState<boolean>(false);
    const [isUploading, setIsUploading] = useState<boolean>(false);

    const generateUniqueId = (): number => {
        const randomNumber: number = generateRandomInt32Number()
        generatedId.current = randomNumber;

        return randomNumber;
    }

    const onSubmit = (data: any): void => {
        setIsUploading(true);
        uploadRef.current?.triggerUpload();
    }

    const onInvalidForm = (errors: any): void => {
        if (errors != null && Object.entries(errors).length > 0) {
            trigger();
        }
    }

    const onFileUploadStatusChange = (event: UploadOnStatusChangeEvent): void => {
        const fileInfo: UploadFileInfo = event.affectedFiles[0];

        if (fileInfo != null) {
            if (fileInfo.status === UploadFileStatus.Uploaded) {
                const response: UploadResponse | undefined = event.response;

                if (response != null) {
                    const fileUploadResponse: IFileUploadResponse | undefined = response.response;
                    if (fileUploadResponse != null) {
                        onAddAttribute(fileUploadResponse.result.streamURL, generatedId.current);
                    }
                }
            }
            else if (fileInfo.status === UploadFileStatus.UploadFailed) {
                setIsFileUploadError(true);
                setIsUploading(false);
            }
        }
        else if (fileInfo == null) {
            setIsFileUploadError(true);
            setIsUploading(false);
        }
        else {
            setIsFileUploadError(false);
        }
    }

    const onFileAdded = (event: UploadOnAddEvent): void => {
        if (isFileUploadError === true && event.affectedFiles.length > 0) {
            setIsFileUploadError(false);
        }
    }

    const onAddAttribute = async (url: string, id: number): Promise<void> => {
        const formValues: any = getValues();
        const response: IAPIResponse<IAttributeCreateResponse> = await createAttribute(lanid, { id, name: formValues['name'], url });
        const formattedResponse = checkAPIResponse<IAttributeCreateResponse>(response);

        if (formattedResponse.status === true) {
            props.onConfirmEvent();
        }
        else {
            log.Error(`Unable to add attribute (${formattedResponse.message})`)
            props.onCancelEvent();
        }
    }

    const getDefaultValueForProperty = (propertyName: string, defaultValue: string) => {
        return defaultValue;
    }

    const doesPropertyHaveErrors = (propertyName: string): boolean => {
        if (errors != null && errors[propertyName] != null) {
            return true;
        }
        else {
            return false;
        }
    }

    const generateErrorText = (property: IGameMainFields): string => {
        if (errors != null && errors[property.name] != null && errors[property.name].type != null) {
            switch (errors[property.name].type) {
                case 'required':
                    return `${property.name} is required`;
                case 'maxLength':
                    return `${property.name} should contain at most ${property.properties.max} characters`;
                case 'minLength':
                    return `${property.name} should contain at least ${property.properties.min} characters`;
                case 'max':
                    return `${property.name} has to have a maximum value of ${property.properties.max}`;
                case 'min':
                    return `${property.name} has to have a minimum value of ${property.properties.min}`;
                case 'pattern':
                    return `Special characters are not allowed`;
                case 'validate':
                    return `An element with this name already exists. Please use a different name`
                default:
                    return ``;
            }
        }
        else {
            return ``;
        }
    }

    useEffect(() => {
        if (errors != null && Object.entries(errors).length > 0) {
            console.error(errors)
        }
    }, [errors])

    return <div className="attributes-upload-popup-container">
        <AnimatedPopup className="attributes-upload-popup-wrapper">
            <div className="attributes-upload-popup-graphic-container">
                <img src={confirmationPopupGraphic} alt='' />
            </div>
            <div className="attributes-upload-popup-title-container">
                <span>{`Upload an attribute`}</span>
            </div>
            <div className="attributes-upload-popup-body-container">
                {attributesValidationInfo.fields.length > 0 &&
                    <>
                        {attributesValidationInfo.fields.map((item: IGameMainFields, index: number) => (
                            <>
                                {item.name === 'name' &&
                                    <>
                                        <div className={`attributes-upload-input-container`}>
                                            <div className={`attributes-upload-input-wrapper ${doesPropertyHaveErrors(item.name) === true ? `error` : ``}`}>
                                                <InputStringController
                                                    control={control}
                                                    item={item}
                                                    getDefaultValue={getDefaultValueForProperty}
                                                    disabled={isUploading}
                                                    uniquenessValidationFunction={null}
                                                />
                                            </div>
                                        </div>
                                        {doesPropertyHaveErrors(item.name) &&
                                            <div className={`attributes-error-container`}>
                                                <span>{generateErrorText(item)}</span>
                                            </div>
                                        }
                                    </>
                                }
                                {item.name === 'url' &&
                                    <div className='attributes-upload-component-container'>
                                        <Upload
                                            ref={uploadRef}
                                            className={`kendo-upload-component-container ${isFileUploadError === true ? `error` : ``}`}
                                            multiple={false}
                                            disabled={isUploading}
                                            showFileList={true}
                                            showActionButtons={false}
                                            restrictions={{ allowedExtensions: ['png', 'svg', 'jpg', 'jpeg'], minFileSize: 1, maxFileSize: 10000000 }}
                                            autoUpload={false}
                                            batch={false}
                                            withCredentials={false}
                                            saveUrl={`${formatURL(config.GamificationEngineBaseURL)}api/v1/game/fileupload/${generateUniqueId()}`}
                                            saveMethod={`POST`}
                                            saveField={`file`}
                                            defaultFiles={[]}
                                            onStatusChange={onFileUploadStatusChange}
                                            onAdd={onFileAdded}
                                        />
                                    </div>
                                }
                            </>
                        ))}
                    </>
                }
            </div>
            <div className="attributes-upload-popup-buttons-container">
                {isUploading === false ?
                    <>
                        <div className='attributes-upload-popup-cancel-button-container'>
                            <span onClick={() => props.onCancelEvent()}>Cancel</span>
                        </div>
                        <div className='attributes-upload-popup-confirm-button-container'>
                            <span onClick={handleSubmit(onSubmit, onInvalidForm)}>Upload</span>
                        </div>
                    </>
                    :
                    <div className='attributes-upload-popup-loading-container'>
                        <FontAwesomeIcon icon={faSpinnerThird} size="3x" color='#9D7EB4' spin />
                    </div>
                }
            </div>
        </AnimatedPopup>
    </div>
}

export const ElementPopup = (props: IElementPopupProps) => {
    const { control, handleSubmit, formState: { errors, dirtyFields }, reset, clearErrors, setValue, unregister, resetField, getValues, trigger, } = useForm();

    const elementsInfo: IndexedElementsInfo = useAppSelector((state) => state.gameProperties.elements);
    const validationInfo: IndexedElementPropertiesInfo = useAppSelector((state) => state.gameProperties.elementPropertiesValidations);
    const attributesInfo: IndexedAttributesInfo = useAppSelector((state) => state.gameProperties.attributes);
    const periodInfo: IndexedPeriodInfo = useAppSelector((state) => state.gameProperties.period);
    const kindInfo: IndexedKindInfo = useAppSelector((state) => state.gameProperties.kind);
    const scopeInfo: IndexedScopeInfo = useAppSelector((state) => state.gameProperties.scope);

    const [errorPopupVisibility, setErrorPopupVisibility] = useState<{ visibility: boolean, message: string }>({ visibility: false, message: '' });
    const [selectedElementType, setSelectedElementType] = useState<IElementsInfo>();
    const [selectedElementProperties, setSelectedElementProperties] = useState<Array<IGameMainFields>>([]);

    const getDefaultElementType = (): IElementsInfo => {
        return {
            id: "",
            name: "Select an element type",
        }
    }

    const getDefaultAttributeType = (): IAttributesInfo => {
        return {
            id: 0,
            name: "Select an attribute",
            url: ""
        }
    }

    const getDefaultPeriod = (): IElementPeriodInfo => {
        return {
            id: '',
            name: 'Select an period',
        }
    }

    const getDefaultKind = (): IElementPeriodInfo => {
        return {
            id: '',
            name: 'Select an kind',
        }
    }

    const getDefaultScope = (): IElementPeriodInfo => {
        return {
            id: '',
            name: 'Select an scope',
        }
    }

    const getTitle = (): string => {
        let title: string = '';

        switch (props.operation) {
            case 'create':
                title = `Add an element`;
                break;
            case 'update':
                title = `Update element details`;
                break;
            case 'view':
                title = `View element details`;
                break;
        }

        return title;
    }

    const getDefaultValueForProperty = (propertyName: string, defaultValue: string): any => {
        if ((props.operation === 'update' || props.operation === 'view') && props.updateElementInfo != null) {
            const elementInfo: IGameElementInfo = props.updateElementInfo;

            switch (propertyName) {
                case 'event':
                    let eventInfo: Array<IGameElementInfo> = [];

                    if (elementInfo.event != null && elementInfo.event.length > 0) {
                        const elemInfo: Array<string> = elementInfo.event;

                        getPointsElementsInfo().forEach((info: IGameElementInfo) => {
                            if (elemInfo.includes(info.name) === true) {
                                eventInfo.push(info);
                            }
                        });
                    }

                    if (eventInfo.length > 0) {
                        return eventInfo;
                    }
                    else {
                        return null;
                    }
                case 'period':
                    if (elementInfo.period != null && elementInfo.period !== "") {
                        return periodInfo[elementInfo.period];
                    }
                    else {
                        return null;
                    }
                case 'scope':
                    if (elementInfo.scope != null && elementInfo.scope !== "") {
                        return scopeInfo[elementInfo.scope];
                    }
                    else {
                        return null;
                    }
                case 'kind':
                    if (elementInfo.kind != null && elementInfo.kind !== "") {
                        return kindInfo[elementInfo.kind];
                    }
                    else {
                        return null;
                    }
                case 'attribute':
                    if (elementInfo.attribute != null) {
                        return attributesInfo[Number(elementInfo.attribute)];
                    }
                    else {
                        return null;
                    }
                default:
                    // @ts-ignore
                    return props.updateElementInfo[propertyName];
            }

        }
        else {
            switch (propertyName) {
                case 'event':
                    const pointsElementsInfo: Array<IGameElementInfo> = getPointsElementsInfo();
                    let matchedDefaultValue: IGameElementInfo | undefined = undefined;

                    if (pointsElementsInfo.length > 0) {
                        pointsElementsInfo.forEach((item: IGameElementInfo) => {
                            if (item.name === defaultValue) {
                                matchedDefaultValue = item;
                            }
                        })
                    }

                    return matchedDefaultValue;
                case 'period':
                    return periodInfo[defaultValue];
                case 'scope':
                    return scopeInfo[defaultValue];
                case 'kind':
                    return kindInfo[defaultValue];
                case 'attribute':
                    return attributesInfo[Number(defaultValue)];
                default:
                    return defaultValue;
            }
        }
    }

    const onElementTypeChange = (event: DropDownListChangeEvent): void => {
        const elementType: IElementsInfo = event.target.value as IElementsInfo;
        const defaultElementType: IElementsInfo = getDefaultElementType();

        const validations: ICoreElementProperties = validationInfo[elementType.id];
        let elementDependencies: Array<string> = [];

        const countOfExistingElements: number = getCountOfExistingElements(elementType.id);

        if (countOfExistingElements >= validationInfo[elementType.id].max) {
            setErrorPopupVisibility({ visibility: true, message: `You have already reached the max limit (${validationInfo[elementType.id].max}) of ${elementType.name} elements that can be created` });
            setSelectedElementType(defaultElementType);
            return;
        }

        if (validations != null && validations.fields != null && validations.fields.length > 0) {
            const elementPropertyFields: Array<IGameMainFields> = validations.fields;

            elementPropertyFields.forEach((item: IGameMainFields, index: number) => {
                if (item.depends != null && item.depends !== '') {
                    elementDependencies.push(item.depends);
                }
            });
        }

        if (elementDependencies.length > 0) {
            let areAllDependenciesMet: boolean = true;

            for (let i = 0; i < elementDependencies.length; i++) {
                if (elementDependencies[i] === "core:point") {
                    if (getPointsElementsInfo().length <= 0) {
                        areAllDependenciesMet = false;
                        setErrorPopupVisibility({ visibility: true, message: `Add a points element before adding a ${elementType.name} element` });
                        break;
                    }
                }
                else if (elementDependencies[i] === "core:attribute") {
                    if (Object.entries(attributesInfo).length <= 0) {
                        areAllDependenciesMet = false;
                        setErrorPopupVisibility({ visibility: true, message: `Add an attribute before adding a ${elementType.name} element` });
                        break;
                    }
                }
            }

            if (areAllDependenciesMet === false) {
                setSelectedElementType(defaultElementType);
                return;
            }
        }

        reset();
        clearErrors();

        setSelectedElementType(event.target.value)
    }

    const onAttributeItemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps): ReactNode => {
        const attributeInfo: IAttributesInfo = itemProps.dataItem as IAttributesInfo;

        const itemChildren = <div className='attribute-cell-container'>
            <div className='attribute-cell-image-container'>
                <img src={attributeInfo.url} alt="" />
            </div>
            <div className='attribute-cell-text-container'>
                <span>{attributeInfo.name}</span>
            </div>
        </div >;

        return React.cloneElement(li, li.props, itemChildren);
    }

    const getDropdownProps = (elementPropertyName: string): DropDownListProps => {
        if (elementPropertyName === 'attribute') {
            return {
                data: Object.values(attributesInfo),
                textField: "name",
                dataItemKey: "id",
                defaultItem: getDefaultAttributeType(),
                itemRender: onAttributeItemRender,
            }
        }
        else if (elementPropertyName === 'period') {
            return {
                data: Object.values(periodInfo),
                textField: "name",
                dataItemKey: "id",
                defaultItem: getDefaultPeriod(),
            }
        }
        else if (elementPropertyName === 'kind') {
            return {
                data: Object.values(kindInfo),
                textField: "name",
                dataItemKey: "id",
                defaultItem: getDefaultKind(),
            }
        }
        else if (elementPropertyName === 'scope') {
            return {
                data: Object.values(scopeInfo),
                textField: "name",
                dataItemKey: "id",
                defaultItem: getDefaultScope(),
            }
        }
        else {
            return {};
        }
    }

    const getMultiSelectProps = (elementPropertyName: string): MultiSelectProps => {
        if (elementPropertyName === 'event') {
            return {
                data: getPointsElementsInfo(),
                textField: "name",
                dataItemKey: "name",
            }
        }
        else {
            return {};
        }
    }

    const onSubmitForm = (data: any): void => {
        if (selectedElementType != null && selectedElementType.id !== "") {
            let elementProperties: any = {};
            const elementValidationProperties: ICoreElementProperties = validationInfo[selectedElementType.id];

            Object.entries(data).forEach((item: [string, any], index: number) => {
                const fieldProperties: IGameMainFields | undefined = elementValidationProperties.fields.find((fieldItem: IGameMainFields) => fieldItem.name === item[0].toString());

                if (fieldProperties != null) {
                    if (fieldProperties.properties.type === 'dropdown-multiple') {
                        elementProperties[item[0]] = [];

                        const selectedEvents: Array<IGameElementInfo> | null = item[1] as Array<IGameElementInfo> | null;

                        if (selectedEvents != null && selectedEvents.length > 0) {
                            selectedEvents.forEach((info: IGameElementInfo, index: number) => {
                                elementProperties[item[0]]?.push(info.name);
                            })
                        }
                    }
                    else if (fieldProperties.properties.type === 'dropdown') {
                        elementProperties[item[0]] = item[1]["id"];
                    }
                    else if (fieldProperties.properties.type === 'number') {
                        elementProperties[item[0]] = Number(item[1]);
                    }
                    else {
                        elementProperties[item[0]] = item[1];
                    }
                }

            });

            elementProperties['id'] = props.operation === 'update' && props.updateElementInfo != null ? props.updateElementInfo.id : Date.now().toString();
            elementProperties['type'] = selectedElementType.id;

            props.onConfirmEvent(elementProperties);
        }
    }

    const onInvalidForm = (errors: any): void => {
        if (errors != null && Object.entries(errors).length > 0) {
            trigger();
        }
    }

    const doesPropertyHaveErrors = (propertyName: string): boolean => {
        if (errors != null && errors[propertyName] != null) {
            return true;
        }
        else {
            return false;
        }
    }

    const generateErrorText = (property: IGameMainFields): string => {
        if (errors != null && errors[property.name] != null && errors[property.name].type != null) {
            switch (errors[property.name].type) {
                case 'required':
                    return `${property.title} is required`;
                case 'maxLength':
                    return `${property.title} should contain at most ${property.properties.max} characters`;
                case 'minLength':
                    return `${property.title} should contain at least ${property.properties.min} characters`;
                case 'max':
                    return `${property.title} has to have a maximum value of ${property.properties.max}`;
                case 'min':
                    return `${property.title} has to have a minimum value of ${property.properties.min}`;
                case 'pattern':
                    return `Special characters are not allowed`;
                case 'validate':
                    return `An element with this name already exists. Please use a different name`
                default:
                    return ``;
            }
        }
        else {
            return ``;
        }
    }

    const checkMultiSelectValue = (propertyName: string, value: any, depends: string | null): undefined | any[] => {
        if (value == null || typeof (value) === 'object') {
            return value;
        }
        else if (typeof (value) === 'string') {
            if (value === '') {
                return undefined;
            }
            else {
                if (depends === 'core:point') {
                    let matchedObjects: Array<any> = [];

                    getPointsElementsInfo().forEach((item: IGameElementInfo, index: number) => {
                        if (item.name === value) {
                            matchedObjects.push(item);
                        }
                    });

                    if (matchedObjects.length > 0) {
                        return matchedObjects;
                    }
                    else {
                        return undefined;
                    }
                }
                else {
                    return undefined;
                }
            }
        }
    }

    const checkDropdownValue = (propertyName: string, value: any, depends: string | null): undefined | any => {
        if (value == null || typeof (value) === 'object') {
            return value;
        }
        else if (typeof (value) === 'string') {
            let matchedObject: any = undefined;

            if (depends === 'core:attribute') {
                matchedObject = attributesInfo[Number(value)];
            }
            else if (propertyName === "period") {
                matchedObject = periodInfo[value];
            }
            else if (propertyName === "kind") {
                matchedObject = kindInfo[value];
            }
            else if (propertyName === "scope") {
                matchedObject = scopeInfo[value];
            }

            return matchedObject;
        }
    }

    const generateForm = (selectedId: string): void => {
        if (selectedId !== '') {
            const elementProperties: ICoreElementProperties = validationInfo[selectedId];

            if (elementProperties != null && elementProperties.fields != null && elementProperties.fields.length > 0) {
                const elementPropertyFields: Array<IGameMainFields> = elementProperties.fields;
                let filteredElementPropertyFields: Array<IGameMainFields> = [];

                elementPropertyFields.forEach((item: IGameMainFields, index: number) => {
                    if (item.name !== 'type') {
                        filteredElementPropertyFields.push(item);
                        setValue(item.name, item.default);
                    }
                })

                reset();
                setSelectedElementProperties([]);

                setTimeout(() => setSelectedElementProperties(filteredElementPropertyFields), 50);
            }
            else {
                reset();
                setSelectedElementProperties([]);
            }
        }
        else {
            reset();
            setSelectedElementProperties([]);
        }
    }

    const getPointsElementsInfo = (): Array<IGameElementInfo> => {
        if (props.existingElementInfo != null && props.existingElementInfo.length > 0) {
            const filteredGames: Array<IGameElementInfo> = props.existingElementInfo.filter((item: IGameElementInfo) => item.type === "core:point");

            if (filteredGames != null && filteredGames.length > 0) {
                let indexedGames: Array<IGameElementInfo> = [];

                filteredGames.forEach((item: IGameElementInfo) => {
                    indexedGames.push(item);
                });

                return indexedGames;
            }
            else {
                return [];
            }
        }
        else {
            return [];
        }
    }

    const getCountOfExistingElements = (elementType: string): number => {
        if (props.existingElementInfo != null && props.existingElementInfo.length > 0 && elementType !== "") {
            return props.existingElementInfo.filter((item: IGameElementInfo) => item.type === elementType).length;
        }
        else {
            return 0;
        }
    }

    const uniquenessInputValidation = (propertyName: string, elementType: string, originalElementInfo: IGameElementInfo | undefined | null, newValue: any): boolean => {
        if (props.existingElementInfo.length > 0) {
            const foundItem: IGameElementInfo | undefined = props.existingElementInfo.find((item: IGameElementInfo) => item.name === newValue);

            if (dirtyFields[propertyName] != null && dirtyFields[propertyName] === true) {
                if (originalElementInfo != null) {
                    //@ts-ignore
                    const originalValue = originalElementInfo[propertyName];

                    if (originalValue !== newValue) {
                        return foundItem != null ? false : true;
                    }
                    else {
                        return true;
                    }
                }
                else {
                    return foundItem != null ? false : true;
                }
            }
            else {
                return true;
            }
        }
        else {
            return true;
        }
    }

    useEffect(() => {
        if (selectedElementType != null) {
            generateForm(selectedElementType.id)
        }
    }, [selectedElementType]);

    useEffect(() => {
        if (errors != null && Object.entries(errors).length > 0 && selectedElementType != null) {
            generateForm(selectedElementType.id)
        }
    }, [errors])

    useEffect(() => {
        if (props.operation === 'update' || props.operation === 'view') {
            if (props.updateElementInfo != null) {
                const selectedElementType: IElementsInfo = elementsInfo[props.updateElementInfo.type];
                setSelectedElementType(selectedElementType);
            }
        }
    }, [props.operation]);

    return <div className="new-element-popup-container">
        <AnimatedPopup className="new-element-popup-wrapper">
            <div className="new-element-popup-graphic-container">
                <img src={confirmationPopupGraphic} alt='' />
            </div>
            <div className="new-element-popup-title-container">
                <span>{getTitle()}</span>
            </div>
            <div className='new-element-popup-form-container'>
                <div className='new-element-popup-dropdown-form-container'>
                    <DropDownList
                        className={`new-element-popup-dropdown`}
                        data={Object.values(elementsInfo)}
                        textField={"name"}
                        dataItemKey={"id"}
                        value={selectedElementType}
                        onChange={onElementTypeChange}
                        popupSettings={{ className: 'new-element-popup-dropdown-popup', popupClass: 'new-element-popup-dropdown-popup-element' }}
                        filterable={false}
                        defaultItem={getDefaultElementType()}
                        size={'medium'}
                        rounded={'large'}
                        fillMode={'solid'}
                        required={true}
                        disabled={props.operation === 'update' || props.operation === 'view'}
                    />
                </div>
                <div className='new-element-popup-form-wrapper'>
                    <div className='form-container'>
                        {selectedElementProperties != null && selectedElementProperties.length > 0 &&
                            <>
                                {selectedElementProperties.map((item: IGameMainFields, index: number) => (
                                    <div className='form-wrapper' key={item.name}>
                                        <div className='form-row'>
                                            <div className={`form-row-title ${doesPropertyHaveErrors(item.name) === true ? `error` : ``}`}>
                                                <span>{item.title}</span>
                                                {/* <Tooltip anchorElement="target" className='tooltip-custom'>
                                                    <FontAwesomeIcon icon={faCircleInfo} color={'#9F9F9F'} title={''} />
                                                </Tooltip> */}
                                            </div>
                                            <div className={`form-row-text-value ${item.properties.type === 'dropdown' || item.properties.type === 'dropdown-multiple' ? `with-dropdown` : ``} ${doesPropertyHaveErrors(item.name) === true ? `error` : ``}`}>
                                                <>
                                                    {item.properties.type === 'string' &&
                                                        <InputStringController
                                                            control={control}
                                                            item={item}
                                                            getDefaultValue={getDefaultValueForProperty}
                                                            disabled={props.operation === 'view'}
                                                            uniquenessValidationFunction={item.properties.unique === true ? (value: any) => uniquenessInputValidation(item.name, item.properties.type, props.updateElementInfo, value) : null}
                                                        />
                                                    }
                                                    {item.properties.type === 'number' &&
                                                        <InputNumberController
                                                            control={control}
                                                            item={item}
                                                            getDefaultValue={getDefaultValueForProperty}
                                                            disabled={props.operation === 'view'}
                                                        />
                                                    }
                                                    {item.properties.type === 'dropdown' &&
                                                        <DropdownController
                                                            control={control}
                                                            item={item}
                                                            getDefaultValue={getDefaultValueForProperty}
                                                            disabled={props.operation === 'view'}
                                                            checkDropdownValue={checkDropdownValue}
                                                            getDropdownProps={getDropdownProps}
                                                            className={`form-row-dropdown-component`}
                                                            popupSettings={{ className: 'form-row-dropdown-popup', popupClass: 'form-row-dropdown-popup-element' }}
                                                        />
                                                    }
                                                    {item.properties.type === 'dropdown-multiple' &&
                                                        <MultiselectController
                                                            control={control}
                                                            item={item}
                                                            getDefaultValue={getDefaultValueForProperty}
                                                            disabled={props.operation === 'view'}
                                                            checkMultiSelectValue={checkMultiSelectValue}
                                                            getMultiSelectProps={getMultiSelectProps}
                                                            className={`form-row-multiselect-component`}
                                                            popupSettings={{ className: 'form-row-dropdown-popup', popupClass: 'form-row-dropdown-popup-element' }}
                                                        />
                                                    }
                                                </>
                                            </div>
                                        </div>
                                        {doesPropertyHaveErrors(item.name) &&
                                            <div className={`form-row-error`}>
                                                <span>{generateErrorText(item)}</span>
                                            </div>
                                        }
                                    </div>
                                ))}
                            </>
                        }
                    </div>
                </div>
            </div>
            <div className="new-element-popup-buttons-container">
                {props.operation !== 'view' ?
                    <>
                        <div className='new-element-popup-cancel-button-container'>
                            <span onClick={() => props.onCancelEvent()}>Cancel</span>
                        </div>
                        <div className='new-element-popup-confirm-button-container'>
                            <span onClick={handleSubmit(onSubmitForm, onInvalidForm)}>Confirm</span>
                        </div>
                    </>
                    :
                    <div className='new-element-popup-close-button-container'>
                        <span onClick={() => props.onCloseEvent()}>Close</span>
                    </div>
                }
            </div>
        </AnimatedPopup >
        <AnimatePresence>
            {errorPopupVisibility.visibility === true &&
                <NotificationPopup
                    message={errorPopupVisibility.message}
                    closePopup={() => setErrorPopupVisibility({ visibility: false, message: '' })}
                />
            }
        </AnimatePresence>
    </div >
}